import { format } from 'date-fns/format';
import queryString from 'query-string';
import { useState } from 'react';
import { toast } from 'sonner';

import { useFetch } from '@/modules/common';
import { AuditReportType } from '@/modules/events/constants';

interface UseExportAuditEntriesParams {
  eventId: string;
  eventName: string;
  stationId?: string;
  stationName?: string;
}

export function useExportAuditEntries({
  eventId,
  eventName,
  stationId,
  stationName,
}: UseExportAuditEntriesParams) {
  const fetchClient = useFetch();
  const [activeAuditType, setActiveAuditType] =
    useState<AuditReportType | null>(null);

  const getFileName = (type: AuditReportType) => {
    const date = format(new Date(), 'yyyy-MM-dd');

    switch (type) {
      case AuditReportType.Station:
        return `${eventName}_station_audit_${date}.csv`;
      case AuditReportType.Products:
        return `${eventName}_${stationName}_product_audit_${date}.csv`;
      default:
        return `${eventName}_audit_${date}.csv`;
    }
  };

  const exportAudit = async (type: AuditReportType) => {
    setActiveAuditType(type);

    try {
      const query = queryString.stringify(
        type === AuditReportType.Products ? { eventId, stationId } : { eventId }
      );
      const response = await fetchClient(`/audit-entries/${type}?${query}`, {
        method: 'GET',
        asFormData: true,
        responseType: 'blob',
      });
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = getFileName(type);

      document.body.appendChild(link);

      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActiveAuditType(null);
    }
  };

  return {
    isExportingAudit: activeAuditType !== null,
    activeAuditType,
    exportAudit,
  } as const;
}
